import type { PropsWithChildren } from "react";

import { UIVersionContext, type UIVersion } from "./UIVersionContext";

/** Props for the UIVersionProvider component */
export interface UIVersionProviderProps extends PropsWithChildren {
  /**
   * The UI version to use throughout the component tree.
   * Defaults to "v2" if not specified.
   * - "v1": Renders components with v1/MUI-compatible styles
   * - "v2": Renders components with new v2 styles
   */
  uiVersion?: UIVersion;
}

/**
 * UIVersionProvider component that provides UI version context to child components.
 *
 * This provider allows v2 components to conditionally render v1 or v2 styles
 * based on the uiVersion prop. This enables gradual migration from v1 to v2
 * components while maintaining backwards compatibility.
 *
 * @example
 * ```tsx
 * // Use v2 styles (default)
 * <UIVersionProvider>
 *   <Button>Click me</Button>
 * </UIVersionProvider>
 *
 * // Use v1-compatible styles
 * <UIVersionProvider uiVersion="v1">
 *   <Button>Click me</Button>
 * </UIVersionProvider>
 * ```
 */
export function UIVersionProvider({ children, uiVersion = "v2" }: UIVersionProviderProps) {
  return <UIVersionContext.Provider value={{ uiVersion }}>{children}</UIVersionContext.Provider>;
}
